import { z } from 'zod'

import { CANCEL_CLAIM_REASON_MAX_LENGTH } from '../constants'

/**
 * Zod schemas for every Reimbursement form (filters, detail, and the claim dialogs).
 * Derived value types live in `../types/schemas`.
 */

/**
 * Reimbursements filter form schema. All fields optional with sensible defaults
 * so that an empty form still parses.
 */
export const reimbursementsFilterSchema = z.object({
  q: z.string().default(''),
  org_id: z.union([z.string(), z.number(), z.literal('')]).default(''),
  companyId: z.union([z.string(), z.number(), z.literal('')]).default(''),
  propertyId: z.union([z.string(), z.number(), z.literal('')]).default(''),
  reimbursement_type: z.array(z.string()).default([]),
  rentalStatus: z.array(z.string()).default([]),
  defaultStatus: z.array(z.string()).default([]),
  claim_status: z.array(z.string()).default([]),
  document_status: z.string().default(''),
  review_decision: z.array(z.string()).default([]),
  past_customer: z.enum(['0', '1', '2', '']).default('1'),
  notification_fromDt: z.string().default(''),
  notification_toDt: z.string().default(''),
  lease_start_fromDt: z.string().default(''),
  lease_start_toDt: z.string().default(''),
  claim_status_fromDt: z.string().default(''),
  claim_status_toDt: z.string().default(''),
})

/**
 * Detail form. Matches Angular `resident-edit-new`: only `rental_status` and
 * `rental_progress` are required (Validators.required). All other fields are
 * optional so a save fires with just those two set — Angular parity.
 */
export const reimbursementsDetailSchema = z
  .object({
    rental_status: z.string().min(1, 'Rental status is required'),
    rental_progress: z.string().min(1, 'Rental progress is required'),
    reimbursement_rental_status: z.string().default(''),
    reimbursement_rental_progress: z.string().default(''),
    reimbursement_type: z.string().default(''),
    /** Unit on application — editable on Resident Information (Angular `resident-edit-new` residentinfo tab). */
    unit_number: z.string().default(''),
    reason_for_default: z.string().default(''),
    rental_status_date: z.string().default(''),
    default_review_status: z.string().default(''),
    default_paid: z.string().default(''),
    default_status_date: z.string().default(''),
    claim_status: z.string().default(''),
    covid_19: z.enum(['', '0', '1']).default(''),
    cdc_declaration: z.enum(['', '0', '1']).default(''),
    payment_plan: z.enum(['', '0', '1']).default(''),
    insurance_review_ready: z.enum(['', '0', '1']).default(''),
    insurance_review_approval: z.enum(['', '0', '1']).default(''),
    liberty_authorization_ready: z.enum(['', '0', '1']).default(''),
    liberty_approved: z.enum(['', '0', '1']).default(''),
    lease_verified: z.enum(['', '0', '1']).default(''),
    skip_claim_affidavit_required: z.enum(['', '0', '1']).default(''),
    is_final_ledger: z.enum(['', '0', '1']).default(''),
    lease_agreement_status: z.string().default(''),
    payment_ledger_status: z.string().default(''),
    unlawful_detainer_status: z.string().default(''),
    skip_claim_affidavit_status: z.string().default(''),
    resident_last_pay_date: z.string().default(''),
    resident_last_pay_amt: z.string().default(''),
    late_date: z.string().default(''),
    notice_of_eviction_date: z.string().default(''),
    ud_filed_date: z.string().default(''),
    unit_vacancy: z.string().default(''),
    date_unit_is_back_on_market: z.string().default(''),
    movein_date: z.string().default(''),
    moveout_date: z.string().default(''),
    new_resident_lease_start_date: z.string().default(''),
    first_month_not_paid_month: z.string().default(''),
    first_month_not_paid_year: z.string().default(''),
    timely: z.string().default('NO'),
    adjusted_notice: z.string().default(''),
    adjust_max_movein_date: z.string().default(''),
    claim_expiration_date: z.string().default(''),
    lease_start_date: z.string().default(''),
    lease_end_date: z.string().default(''),
    unused_security_deposit: z.string().default(''),
    partial_payment_amount: z.string().default(''),
    loss_adjustment: z.string().default(''),
    net_amount: z.string().default(''),
    notes: z.string().default(''),
    message: z.string().default(''),
    /** Calculation tab — Angular `lease_month` / lease age (months). */
    lease_month: z.string().default(''),
    /** Calculation tab — Angular `adjusted_reason` (not public `notes`). */
    adjusted_reason: z.string().default(''),
  })

export const cancelClaimSchema = z
  .object({
    default_claim: z.string().default('cancel_claim'),
    reason_note: z
      .string()
      .max(
        CANCEL_CLAIM_REASON_MAX_LENGTH,
        `Reason must be ${CANCEL_CLAIM_REASON_MAX_LENGTH} characters or fewer`,
      ).default(''),
  })
  .superRefine((values, ctx) => {
    if (values.default_claim === 'cancel_claim' && values.reason_note.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['reason_note'],
        message: 'Reason is required',
      })
    }
  })

export const denyReopenClaimSchema = z.object({
  reason_note: z
      .string()
      .max(
        CANCEL_CLAIM_REASON_MAX_LENGTH,
        `Reason must be ${CANCEL_CLAIM_REASON_MAX_LENGTH} characters or fewer`,
      ).default(''),
})

export const paymentFormSchema = z.object({
  default_paid: z.string().default(''),
  /** Angular adjust-payment field (`adjustment_amount`). */
  adjustment_amount: z.string().default(''),
  resident_last_pay_amt: z.string().min(1, 'Resident last pay amount is required'),
  payment_source: z.string().min(1, 'Payment source is required'),
  payment_date: z.string().min(1, 'Payment date is required'),
  partial_payment_amount: z.string().default(''),
  unused_security_deposit: z.string().default(''),
  transaction_notes: z.string().default(''),
  payment_notes: z.string().default(''),
  check_number: z.string().default(''),
  charge_processing_fee: z.boolean().default(false),
  already_completed: z.boolean().default(false),
  payment_managed: z.string().default(''),
  property_name: z.string().default(''),
  pay_to: z.string().default(''),
  verify: z.string().default(''),
  payee_name: z.string().default(''),
  bank_name: z.string().default(''),
  routing_number: z.string().default(''),
  account_number: z.string().default(''),
  payment_address: z.string().default(''),
  city_state_zip: z.string().default(''),
})

export const reviewClaimSchema = z.object({
  claim_type: z.string().min(1, 'Claim type is required'),
  notes: z.string().default(''),
  reasons: z.object({
    lease_end: z.boolean().default(false),
    partial_pay_delay_eviction: z.boolean().default(false),
    days_pass: z.boolean().default(false),
    incomplete_document: z.boolean().default(false),
  }),
  verification_reasons: z.object({
    moveindate: z.boolean().default(false),
    updated_ledger: z.boolean().default(false),
    maximum_claim: z.boolean().default(false),
    lease_expire: z.boolean().default(false),
    ledger_discrepancy: z.boolean().default(false),
  }),
  limiting_factors: z.object({
    multiple_payment: z.boolean().default(false),
    partial_pay_delay_eviction: z.boolean().default(false),
    delay_eviction: z.boolean().default(false),
    exceed_vacancy: z.boolean().default(false),
    missing_document: z.boolean().default(false),
    discrepancy: z.boolean().default(false),
    impact_covid: z.boolean().default(false),
    base_on_covid: z.boolean().default(false),
  }),
})

export const reviewResidentSchema = z.object({
  notification_date: z.string().default(''),
  notes: z.string().default(''),
  lease_agreement: z.boolean().default(false),
  final_payment_ledger: z.boolean().default(false),
  legal_forms: z.boolean().default(false),
})

export const statementPopupSchema = z.object({
  include_statement: z.boolean().default(false),
  include_instruction: z.boolean().default(false),
  include_signup_instruction: z.boolean().default(false),
  email_sendto: z.string().default(''),
  email_sendto_property: z.string().default(''),
})

export const reasonSchema = z.object({
  note: z.string().min(1, 'Reason is required'),
})

export const MOVE_OUT_DATE_REQUIRED_MESSAGE = 'Move out date is required'

export const leaseEndDateSchema = z.object({
  moveout_date: z.string().min(1, MOVE_OUT_DATE_REQUIRED_MESSAGE),
})
